import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Home from "@mui/icons-material/Home";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Drawer,
  FormControlLabel,
  Switch,
  IconButton,
  Link,
  Typography,
  useTheme,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getWebSocketUrl } from "src/config";
import LiveStreamLinksModal from "src/shared/ui/src/modals/LiveStreamLinksModal";
import type { LiveStreamLinksSavePayload } from "src/shared/ui/src/modals/LiveStreamLinksModal";
import { useSnack } from "src/shared/ui/src/contexts/SnackContext";
import {
  recalculateMeetRankings,
  listMeetPlateauNumbers,
  MeetWithAll,
  resolveLiveStreamUrl,
} from "src/shared/util/src";
import dico from "src/shared/util/src/language/dico";
import { userHasLicenseAuthorization } from "src/shared/util/src/license/licenseAuthorizations";
import { useMeetStore } from "src/stores/meetStore";
import { useUserStore } from "src/stores/userStore";

interface MeetDrawerProps {
  open: boolean;
  onClose: () => void;
  meet: MeetWithAll;
}

const MeetDrawer = ({ open, onClose, meet }: MeetDrawerProps) => {
  const { currentUser, token, fetchUser } = useUserStore();
  const { showSnack } = useSnack();
  const { updateMeetWithAll, setMeetPublic } = useMeetStore();
  const theme = useTheme();
  const [savingRankingMode, setSavingRankingMode] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [openStreamLinks, setOpenStreamLinks] = useState(false);
  const [savingStreamLinks, setSavingStreamLinks] = useState(false);
  const [searchParams] = useSearchParams();
  const selectedGroupId = searchParams.get("group");

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    if (token && !currentUser) {
      fetchUser(token);
    }
  }, [fetchUser, token, currentUser]);

  const isAdmin =
    currentUser?.userRoles.some((relation) => relation.role.name === "Admin") ??
    false;
  const currentUserLicenseKey = currentUser?.license?.key ?? null;
  const isMeetOwner =
    currentUserLicenseKey !== null && meet.licenseKey === currentUserLicenseKey;
  const canAccessRuntimeLayouts = isAdmin || isMeetOwner;
  const canAccessRemoteCustomDisplay = useMemo(
    () => userHasLicenseAuthorization(currentUser, "remoteCustomDisplay"),
    [currentUser],
  );
  const canShowRemoteRuntimeLinks =
    canAccessRuntimeLayouts && canAccessRemoteCustomDisplay;
  const selectedGroup = meet.groups.find((g) => g.selected);
  const resolvedLiveUrl = resolveLiveStreamUrl(
    meet,
    selectedGroup?.plateauNumber,
  );
  const plateauNumbers = useMemo(
    () =>
      listMeetPlateauNumbers(
        meet.groups,
        (
          meet as MeetWithAll & {
            plateauStreams?: Array<{ plateauNumber: number; url: string }>;
          }
        ).plateauStreams,
      ),
    [meet],
  );

  const handleSaveStreamLinks = async (payload: LiveStreamLinksSavePayload) => {
    if (!token) {
      return;
    }
    setSavingStreamLinks(true);
    try {
      const streamUrlsByPlateau: Record<string, string> = {};
      for (const row of payload.plateauStreams) {
        streamUrlsByPlateau[String(row.plateauNumber)] = row.url;
      }
      const response = await fetch(
        `${getWebSocketUrl()}/meets/${meet.id}/stream-links`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            streamUrl: payload.streamUrl,
            streamUrlsByPlateau,
          }),
        },
      );
      if (!response.ok) {
        throw new Error("STREAM_LINKS_SAVE_FAILED");
      }
      const updated = (await response.json()) as MeetWithAll;
      // Close first so parent meet refresh does not flash the open dialog
      setOpenStreamLinks(false);
      updateMeetWithAll({
        ...meet,
        streamUrl: updated.streamUrl,
        plateauStreams: updated.plateauStreams ?? [],
      });
      showSnack(dico().stream.plateauLinks.saved, "success");
    } catch {
      showSnack(dico().stream.plateauLinks.saveError, "error");
    } finally {
      setSavingStreamLinks(false);
    }
  };

  const handleRankingModeChange = async (isNational: boolean) => {
    if (!token) return;
    setSavingRankingMode(true);
    try {
      const response = await fetch(
        `${getWebSocketUrl()}/meets/${meet.id}/ranking-mode`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isNational }),
        },
      );
      if (!response.ok) throw new Error("RANKING_MODE_SAVE_FAILED");
      const updated = await response.json();
      const nextMeet = {
        ...meet,
        isNational: updated.isNational,
        groups: meet.groups.map((group) => ({
          ...group,
          athletes: group.athletes.map((athlete) => ({ ...athlete })),
        })),
      };
      recalculateMeetRankings(nextMeet);
      updateMeetWithAll(nextMeet);
    } catch {
      showSnack(dico().errors.anErrorOccured, "error");
    } finally {
      setSavingRankingMode(false);
    }
  };

  const setMeetIsPublic = (isPublic: boolean) => {
    if (!meet.id || !token || !meet) return;
    setMeetPublic(meet.id, isPublic, token).then((ok) => {
      if (ok) {
        meet.isPublic = isPublic;
        updateMeetWithAll(meet);
        showSnack(
          isPublic ? dico().meetDefinedPublic : dico().meetDefinedPrivate,
          "success",
        );
      } else {
        showSnack(dico().errors.anErrorOccured, "error");
      }
    });
  };

  const getDefinePublicButtons = () => {
    if (!isAdmin || !meet) return <></>;
    return meet.isPublic ? (
      <Button
        variant="outlined"
        color="error"
        onClick={() => setMeetIsPublic(false)}
        fullWidth
      >
        {dico().definePrivate}
      </Button>
    ) : (
      <Button
        variant="outlined"
        onClick={() => setMeetIsPublic(true)}
        fullWidth
      >
        {dico().definePublic}
      </Button>
    );
  };

  return (
    <AnimatePresence mode="wait">
      {open && (
        <Drawer
          anchor="right"
          open={open}
          onClose={onClose}
          variant="temporary"
          slotProps={{
            backdrop: {
              sx: {
                backgroundColor: "transparent",
              },
            },
          }}
          PaperProps={{
            component: motion.div,
            sx: {
              width: { xs: "100%", sm: "50%", md: "35%" },
              bgcolor: "background.paper",
              borderLeft: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[8],
            },
            initial: { x: "100%" },
            animate: { x: 0 },
            exit: { x: "100%" },
            transition: {
              type: "spring",
              stiffness: 250,
              damping: 25,
              mass: 1,
            },
          }}
          SlideProps={{
            timeout: {
              enter: 300,
              exit: 200,
            },
          }}
        >
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.1 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "auto",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
              }}
            >
              <Typography variant="h6" component="div">
                {dico().meets}
              </Typography>
              <IconButton
                onClick={onClose}
                sx={{
                  color: "inherit",
                  "&:hover": {
                    bgcolor: theme.palette.primary.dark,
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Content */}
            <Box
              sx={{
                flex: 1,
                p: 2,
                display: "flex",
                flexDirection: "column",
                "& a": {
                  color: theme.palette.text.primary,
                  textDecoration: "none",
                  transition: theme.transitions.create("all", {
                    duration: theme.transitions.duration.shorter,
                  }),
                  "&:hover": {
                    color: theme.palette.primary.main,
                  },
                },
              }}
            >
              <Link
                href={`/app/${meet.id}`}
                sx={{
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Home
                  sx={{
                    fontSize: 24,
                    mb: 1,
                    color: theme.palette.primary.main,
                  }}
                />
                <Typography variant="h5" color="primary" gutterBottom>
                  {meet.name}
                </Typography>
              </Link>

              {/* Live Stream Link - Only if a resolved URL exists for the selected group */}
              {selectedGroup && resolvedLiveUrl && (
                <Link
                  href={`/app/${meet.id}/live`}
                  sx={{
                    mb: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.5,
                    borderRadius: 1,
                    border: `2px solid ${theme.palette.primary.main}`,
                    backgroundColor: theme.palette.action.hover,
                    transition: theme.transitions.create(
                      ["background-color", "transform", "border-color"],
                      {
                        duration: theme.transitions.duration.shorter,
                      },
                    ),
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main,
                      transform: "translateX(4px)",
                      "& .MuiTypography-root": {
                        color: theme.palette.primary.contrastText,
                      },
                      "& .MuiSvgIcon-root": {
                        color: theme.palette.primary.contrastText,
                      },
                    },
                  }}
                >
                  <LiveTvIcon
                    sx={{
                      fontSize: 24,
                      color: theme.palette.primary.main,
                    }}
                  />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      color: theme.palette.primary.main,
                    }}
                  >
                    {dico().stream.liveGroup}
                  </Typography>
                </Link>
              )}

              {canAccessRuntimeLayouts && (
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    label={dico().meet.isNational}
                    control={
                      <Switch
                        checked={meet.isNational ?? false}
                        disabled={savingRankingMode}
                        onChange={(_, checked) =>
                          void handleRankingModeChange(checked)
                        }
                      />
                    }
                  />
                </Box>
              )}

              {/* Groups Accordion */}
              <Accordion
                expanded={expanded}
                onChange={handleAccordionChange}
                sx={{
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  "&:before": {
                    display: "none",
                  },
                  "& .MuiAccordion-root": {
                    margin: 0,
                  },
                  "&.Mui-expanded": {
                    margin: 0,
                  },
                  "& .MuiAccordionSummary-root": {
                    minHeight: 0,
                    "&.Mui-expanded": {
                      minHeight: 0,
                    },
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    padding: 0,
                    minHeight: "unset",
                    "& .MuiAccordionSummary-content": {
                      margin: 0,
                      "&.Mui-expanded": {
                        margin: 0,
                      },
                    },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      transform: expanded ? "rotate(-180deg)" : "rotate(0)",
                      transition: theme.transitions.create("transform", {
                        duration: theme.transitions.duration.shorter,
                      }),
                    },
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    {dico().group}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0, mt: 2 }}>
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    sx={{ ml: 2 }}
                  >
                    {meet.groups.map((group) => (
                      <Link
                        key={group.id}
                        href={`/app/${meet.id}?group=${group.id}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 1.5,
                          borderRadius: 1,
                          position: "relative",
                          backgroundColor:
                            selectedGroupId === group.id
                              ? theme.palette.action.selected
                              : "transparent",
                          transition: theme.transitions.create(
                            ["background-color", "transform"],
                            {
                              duration: theme.transitions.duration.shorter,
                            },
                          ),
                          "&:hover": {
                            backgroundColor:
                              selectedGroupId === group.id
                                ? theme.palette.action.selected
                                : theme.palette.action.hover,
                            transform: "translateX(4px)",
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: -4,
                            top: "50%",
                            width: 3,
                            height: "70%",
                            bgcolor: theme.palette.primary.main,
                            transform:
                              selectedGroupId === group.id
                                ? "translateY(-50%) scaleY(1)"
                                : "translateY(-50%) scaleY(0)",
                            transition: theme.transitions.create("transform", {
                              duration: theme.transitions.duration.shorter,
                            }),
                            borderRadius: "0 4px 4px 0",
                          },
                          "&:hover::before": {
                            transform: "translateY(-50%) scaleY(1)",
                          },
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight:
                              selectedGroupId === group.id ? 500 : 400,
                          }}
                        >
                          {group.name}
                        </Typography>
                      </Link>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>

            {/* Owner/admin: configure live links */}
            {canAccessRuntimeLayouts && (
              <Box
                sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}
              >
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<LiveTvIcon />}
                  onClick={() => setOpenStreamLinks(true)}
                  sx={{
                    textTransform: "none",
                    py: 1,
                    justifyContent: "flex-start",
                  }}
                >
                  {dico().stream.plateauLinks.menuButton}
                </Button>
              </Box>
            )}

            {/* Private runtime links (owner/admin + remote display license) */}
            {canShowRemoteRuntimeLinks && (
              <Box
                sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 2,
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  {dico().widget.runtimeSectionTitle}
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Button
                    component={Link}
                    href={`/app/${meet.id}/widget`}
                    variant="contained"
                    color="secondary"
                    fullWidth
                    startIcon={<SettingsIcon />}
                    sx={{
                      textTransform: "none",
                      py: 1,
                      justifyContent: "flex-start",
                    }}
                  >
                    {dico().widget.runtimeWidgetsButton}
                  </Button>
                  <Button
                    component={Link}
                    href={`/app/${meet.id}/display`}
                    variant="outlined"
                    fullWidth
                    startIcon={<LiveTvIcon />}
                    sx={{
                      textTransform: "none",
                      py: 1,
                      justifyContent: "flex-start",
                    }}
                  >
                    {dico().widget.runtimeDisplaysButton}
                  </Button>
                </Box>
              </Box>
            )}
            {/* Admin visibility controls */}
            {isAdmin && (
              <Box
                sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}
              >
                {getDefinePublicButtons()}
              </Box>
            )}
            <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              {/* Local Widgets URLs */}
              {meet.localWidgetsURLs && meet.localWidgetsURLs.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                    }}
                  >
                    {dico().widget.localWidgets}
                  </Typography>
                  {meet.localWidgetsURLs.map((url, index) => (
                    <Button
                      key={index}
                      component="a"
                      href={`http://${url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      fullWidth
                      startIcon={<OpenInNewIcon />}
                      sx={{
                        textTransform: "none",
                        py: 1,
                        mb: 1,
                        justifyContent: "flex-start",
                        "&:last-child": {
                          mb: 0,
                        },
                      }}
                    >
                      {url}
                    </Button>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Drawer>
      )}
      <LiveStreamLinksModal
        open={openStreamLinks}
        onClose={() => setOpenStreamLinks(false)}
        saving={savingStreamLinks}
        initialStreamUrl={meet.streamUrl}
        initialPlateauStreams={meet.plateauStreams ?? []}
        plateauNumbers={plateauNumbers}
        onSave={handleSaveStreamLinks}
      />
    </AnimatePresence>
  );
};

export default MeetDrawer;
